import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../components/Button";
import Input, { MaterialInputEvent } from "../../../components/v2/Input";
import Layout from "../../../components/v2/Layout";
import Select, { MaterialSelectEvent } from "../../../components/v2/Select";
import Format from "../../../helpers/Format";
import TransparenciaService, {
  Auxilios,
  Honorarios,
  Periodo,
  Procurador,
  ValoresProcurador,
} from "../../../services/TransparenciaService";

import style from "./styles.module.scss";

const keyI18n = "translations.transparency";
const MIN_CHARS = 3;

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

function ValueField({ label, value }: { label: string; value: number | null }) {
  return (
    <div className={style.value}>
      <span className={`pgm-body-medium ${style.valueLabel}`}>{label}</span>
      <span className={`pgm-title-medium ${style.valueAmount}`}>
        {Format.currency(value ?? 0)}
      </span>
    </div>
  );
}

export default function Transparencia() {
  const { t } = useTranslation();

  const [procuradores, setProcuradores] = useState<Procurador[]>([]);
  const [periodos, setPeriodos] = useState<Periodo[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [valores, setValores] = useState<ValoresProcurador | null>(null);
  const [loadingValores, setLoadingValores] = useState(false);
  const [query, setQuery] = useState("");
  const [month, setMonth] = useState<number>(0);
  const [year, setYear] = useState<number>(0);
  const [search, setSearch] = useState<string | null>(null);
  const [minError, setMinError] = useState(false);
  const [selected, setSelected] = useState<Procurador>();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    Promise.all([TransparenciaService.procuradores(), TransparenciaService.periodos()])
      .then(([listaProcuradores, listaPeriodos]) => {
        if (!Array.isArray(listaProcuradores) || !Array.isArray(listaPeriodos)) {
          throw new Error("invalid transparency response");
        }

        setProcuradores(listaProcuradores);
        setPeriodos(listaPeriodos);
        const ultimo = listaPeriodos[listaPeriodos.length - 1];
        if (ultimo) {
          setMonth(ultimo.mes);
          setYear(ultimo.ano);
        }
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected || !month || !year) {
      setValores(null);
      return;
    }

    setLoadingValores(true);
    setValores(null);
    TransparenciaService.valores(selected.matricula, month, year)
      .then((resposta) => setValores(resposta && typeof resposta === "object" ? resposta : null))
      .catch(() => setValores(null))
      .finally(() => setLoadingValores(false));
  }, [selected, month, year]);

  const anosDisponiveis = useMemo(
    () => Array.from(new Set(periodos.map((item) => item.ano))).sort((a, b) => a - b),
    [periodos],
  );

  const mesesDisponiveis = (ano: number) =>
    periodos
      .filter((item) => item.ano === ano)
      .map((item) => item.mes)
      .sort((a, b) => a - b);

  const periodoAtual = periodos.find((item) => item.mes === month && item.ano === year);

  const results = useMemo(() => {
    if (search === null) {
      return null;
    }

    return procuradores.filter((item) => normalize(item.nome).includes(search));
  }, [search, procuradores]);

  const handleSearch = () => {
    const term = normalize(query);

    if (term.length < MIN_CHARS) {
      setMinError(true);
      setSearch(null);
      setSelected(undefined);
      return;
    }

    setMinError(false);
    setSelected(undefined);
    setSearch(term);
  };

  const handleMonthChange = (event: MaterialSelectEvent) => {
    setMonth(Number(event.target.value));
  };

  const handleYearChange = (event: MaterialSelectEvent) => {
    const novoAno = Number(event.target.value);
    setYear(novoAno);

    const meses = mesesDisponiveis(novoAno);
    if (meses.length > 0 && !meses.includes(month)) {
      setMonth(meses[meses.length - 1]);
    }
  };

  const monthOptions = mesesDisponiveis(year).map((mes) => ({
    value: mes,
    label: MONTHS[mes - 1],
  }));
  const yearOptions = anosDisponiveis.map((value) => ({ value, label: String(value) }));

  const renderHonorarios = (honorarios: Honorarios, legal: string | null) => (
    <section className={style.card}>
      <h3 className={`pgm-title-medium ${style.cardTitle}`}>
        <span aria-hidden="true" className={`material-symbols-sharp ${style.cardIcon}`}>
          payments
        </span>
        {t(`${keyI18n}.fees.title`)}
      </h3>
      <div className={style.values}>
        <ValueField label={t(`${keyI18n}.fees.gross`)} value={honorarios.bruto} />
        <ValueField label={t(`${keyI18n}.fees.otherDeductions`)} value={honorarios.outrasDeducoes} />
        <ValueField label={t(`${keyI18n}.fees.tax`)} value={honorarios.irRetido} />
        <ValueField label={t(`${keyI18n}.fees.net`)} value={honorarios.liquido} />
      </div>
      {legal && (
        <div className={style.legal}>
          <p className={`pgm-label-large ${style.legalTitle}`}>{t(`${keyI18n}.legal.title`)}:</p>
          <p className={`pgm-body-small ${style.legalText}`}>{legal}</p>
        </div>
      )}
    </section>
  );

  const renderAuxilios = (auxilios: Auxilios, legal: string | null) => (
    <section className={style.card}>
      <h3 className={`pgm-title-medium ${style.cardTitle}`}>
        <span aria-hidden="true" className={`material-symbols-sharp ${style.cardIcon} ${style.cardIconAids}`}>
          volunteer_activism
        </span>
        {t(`${keyI18n}.aids.title`)}
      </h3>
      <div className={style.values}>
        <ValueField label={t(`${keyI18n}.aids.meal`)} value={auxilios.alimentacao} />
        <ValueField label={t(`${keyI18n}.aids.health`)} value={auxilios.saude} />
      </div>
      {legal && (
        <div className={style.legal}>
          <p className={`pgm-label-large ${style.legalTitle}`}>{t(`${keyI18n}.legal.title`)}:</p>
          <p className={`pgm-body-small ${style.legalText}`}>{legal}</p>
        </div>
      )}
    </section>
  );

  const renderNotice = (message: string) => (
    <div className={style.notice} role="status">
      <span aria-hidden="true" className={`material-symbols-sharp ${style.noticeIcon}`}>
        info
      </span>
      <p className={`pgm-body-medium ${style.noticeText}`}>{message}</p>
    </div>
  );

  const renderDetail = (item: Procurador) => {
    const periodo = periodoAtual;
    const honorarios = valores ? valores.honorarios : null;
    const auxilios = valores ? valores.auxilios : null;
    const noneRepasse = !honorarios && !auxilios;

    return (
      <div className={style.detail}>
        <div className={style.detailHeader}>
          <Button
            variant="text"
            shape="round"
            size="small"
            iconButton
            aria-label={t(`${keyI18n}.results.back`)}
            onClick={() => setSelected(undefined)}
            icon={<span className="material-symbols-sharp">arrow_back</span>}
          />
          <h2 className={`pgm-title-medium ${style.detailName}`}>{item.nome.toUpperCase()}</h2>
        </div>

        {loadingValores ? (
          renderNotice(t(`${keyI18n}.loading`))
        ) : noneRepasse ? (
          renderNotice(t(`${keyI18n}.warnings.none`))
        ) : (
          <>
            {honorarios && renderHonorarios(honorarios, periodo ? periodo.observacaoHonorarios : null)}
            {!honorarios && renderNotice(t(`${keyI18n}.warnings.noFees`))}
            {auxilios && renderAuxilios(auxilios, periodo ? periodo.observacaoAuxilios : null)}
            {!auxilios && renderNotice(t(`${keyI18n}.warnings.noAids`))}
          </>
        )}

        <p className={`pgm-body-medium ${style.portal}`}>
          {t(`${keyI18n}.portal.text`)}{" "}
          <a
            className={style.portalLink}
            href="https://transparencia.fortaleza.ce.gov.br/"
            rel="noopener noreferrer"
            target="_blank"
          >
            {t(`${keyI18n}.portal.link`)}
          </a>
          <span aria-hidden="true" className={`material-symbols-sharp ${style.portalIcon}`}>
            open_in_new
          </span>
        </p>
      </div>
    );
  };

  const renderResults = () => {
    if (!results) {
      return null;
    }

    if (results.length === 0) {
      return renderNotice(t(`${keyI18n}.notFound`, { term: query.trim() }));
    }

    return (
      <div className={style.results}>
        <h2 className={`pgm-title-medium ${style.resultsTitle}`}>
          {t(`${keyI18n}.results.count`, { count: results.length })}
        </h2>
        <ul className={style.resultsGrid}>
          {results.map((item) => (
            <li key={item.matricula}>
              <button
                className={style.resultCard}
                onClick={() => setSelected(item)}
                type="button"
              >
                <span className={`pgm-title-small ${style.resultName}`}>
                  {item.nome.toUpperCase()}
                </span>
                <span className={`pgm-label-large ${style.resultView}`}>
                  {t(`${keyI18n}.results.view`)}
                  <span aria-hidden="true" className={`material-symbols-sharp ${style.resultViewIcon}`}>
                    arrow_forward
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <Layout title={t(`${keyI18n}.title`)}>
      <div className={style.body}>
        <section className={style.searchContainer}>
          <header className={style.header}>
            <h1 className={`pgm-headline-small ${style.title}`}>{t(`${keyI18n}.title`)}</h1>
            <Button
              variant="text"
              shape="round"
              size="small"
              iconButton
              className={style.collapseToggle}
              aria-expanded={!collapsed}
              aria-label={t(`${keyI18n}.title`)}
              onClick={() => setCollapsed((value) => !value)}
              icon={
                <span className="material-symbols-sharp">
                  {collapsed ? "arrow_drop_down" : "arrow_drop_up"}
                </span>
              }
            />
          </header>
          <p
            className={[
              `pgm-body-large ${style.subtitle}`,
              collapsed ? style.subtitleCollapsed : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {t(`${keyI18n}.subtitle`)}
          </p>

          {!collapsed && (
          <div className={style.filters}>
            <div className={style.nameField}>
              <Input
                autoComplete="off"
                error={minError}
                fullWidth
                label={t(`${keyI18n}.fields.name`)}
                leadingIcon={<span className="material-symbols-sharp">person_search</span>}
                name="procurador"
                onInput={(event: MaterialInputEvent) => {
                  setQuery(event.target.value);
                  if (minError) {
                    setMinError(false);
                  }
                }}
                onKeyDown={(event: React.KeyboardEvent) => {
                  if (event.key === "Enter") {
                    handleSearch();
                  }
                }}
                value={query}
                variant="filled"
              />
              <span
                className={[
                  `pgm-body-small ${style.nameHelper}`,
                  minError ? style.nameHelperError : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {t(`${keyI18n}.minChars`)}
              </span>
            </div>

            <Select
              className={style.periodSelect}
              label={t(`${keyI18n}.fields.month`)}
              name="mes"
              onChange={handleMonthChange}
              options={monthOptions}
              value={month}
              variant="filled"
            />

            <Select
              className={style.periodSelect}
              label={t(`${keyI18n}.fields.year`)}
              name="ano"
              onChange={handleYearChange}
              options={yearOptions}
              value={year}
              variant="filled"
            />

            <Button
              className={style.searchButton}
              variant="tonal"
              shape="round"
              size="small"
              fullWidth
              onClick={handleSearch}
              icon={<span className="material-symbols-sharp">search</span>}
            >
              {t(`${keyI18n}.search`)}
            </Button>
          </div>
          )}
        </section>

        {loadError
          ? renderNotice(t(`${keyI18n}.loadError`))
          : loading
          ? renderNotice(t(`${keyI18n}.loading`))
          : selected
          ? renderDetail(selected)
          : results
          ? renderResults()
          : (
            <div className={style.emptyState}>
              <span aria-hidden="true" className={`material-symbols-sharp ${style.emptyIcon}`}>
                person_search
              </span>
              <p className={`pgm-body-large ${style.emptyText}`}>{t(`${keyI18n}.emptyState`)}</p>
            </div>
          )}
      </div>
    </Layout>
  );
}
